import { Trash } from 'iconoir-react';
import parseInt from 'lodash/parseInt';
import React, {
  ChangeEventHandler,
  FunctionComponent,
  MouseEventHandler,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 } from 'uuid';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { SUPPORTED_COMPONENTS } from '@savgroup-front-common/types';

import { SafeFormattedMessage } from '../../../formatters';
import { useCombinedRefs } from '../../../hooks';
import { MinusIcon, PlusIcon } from '../../../protons/icons';
import { FieldMessage } from '../common';
import { getFinalFieldState } from '../common/helpers/getFinalFieldState';
import { Label } from '../common/Label/Label';
import Input from '../Input';
import { InputProps } from '../Input/Input.types';

import messages from './messages';
import {
  $MinusButtonQuantity,
  $PlusButtonQuantity,
  $QuantityContainer,
  $QuantityDisplay,
} from './QuantityInput.styles';

const DIGITS_ONLY = /^\d+$/;

const stopPropagation: MouseEventHandler = (e) => e.stopPropagation();

interface QuantityInputProps extends Omit<InputProps, 'onChange'> {
  id?: string;
  hiddenMax?: number;
  max?: number;
  min?: number;
  disabled?: boolean;
  onChange: (newValue: number) => void;
  selectAllOnFocus?: boolean;
  fullWidth?: boolean;
  isSmall?: boolean;
}

interface QuantityInputPropsRef extends QuantityInputProps {
  forwardedRef: Ref<HTMLInputElement>;
}

const QuantityInput: FunctionComponent<
  React.PropsWithChildren<QuantityInputPropsRef>
> = ({
  name = 'quantity',
  onChange,
  max,
  value = 0,
  min = 0,
  disabled = false,
  isDisabled = false,
  label,
  isRequired,
  forwardedRef,
  hiddenMax = 99,
  postLabel,
  errors = {},
  isError = false,
  warnings = {},
  isWarning = false,
  successes = {},
  isSuccess = false,
  selectAllOnFocus = false,
  dataTestId,
  fullWidth,
  isSmall = false,
}) => {
  const transitionDisabled = isDisabled || disabled;
  const [status, message] = getFinalFieldState({
    errors: { isStatus: isError, messages: errors },
    warnings: { isStatus: isWarning, messages: warnings },
    successes: { isStatus: isSuccess, messages: successes },
    name,
  });

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [currentQuantity, setCurrentQuantity] = useState<string>(
    value.toString(),
  );

  const customMax = max || hiddenMax;
  const isDeleteStep = currentQuantity === '1' && min === 0;

  const idFallback = useMemo(() => v4(), []);

  const isValid = (input: number) => {
    const isMinValid = input >= min;
    const isMaxValid = input <= customMax;

    return isMinValid && isMaxValid;
  };

  const handleDecrementQuantity = () => {
    if (transitionDisabled) {
      return;
    }

    const newQuantity = parseInt(currentQuantity) - 1;

    if (isValid(newQuantity)) {
      onChange(newQuantity);
      setCurrentQuantity(newQuantity.toString());
    }
  };

  const handleIncrementQuantity = () => {
    if (transitionDisabled) {
      return;
    }

    const newQuantity = parseInt(currentQuantity) + 1;

    if (isValid(newQuantity)) {
      onChange(newQuantity);
      setCurrentQuantity(newQuantity.toString());
    }
  };

  const handleEditQuantity: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (transitionDisabled) {
      return;
    }
    const current = e.target?.value?.trim();

    if (current === undefined || current === '') {
      setCurrentQuantity('');

      return;
    }
    if (!DIGITS_ONLY.test(current)) {
      return;
    }

    const newQuantity = parseInt(current);

    if (isValid(newQuantity)) {
      onChange(newQuantity);
      setCurrentQuantity(newQuantity.toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);

    if (currentQuantity === '') {
      setCurrentQuantity(value.toString());
    }
  };

  useEffect(() => {
    setCurrentQuantity(value.toString());
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);
  const combinedRef = useCombinedRefs(inputRef, forwardedRef);

  return (
    <>
      {label && (
        <Label
          htmlFor={name || idFallback}
          isRequired={isRequired}
          status={status}
          postLabel={postLabel}
        >
          {SafeFormattedMessage(label)}
        </Label>
      )}
      <$QuantityContainer
        onClick={stopPropagation}
        $status={status}
        $isFocused={isFocused}
        $isHovered={isHovered}
        data-testid={dataTestId}
        fullWidth={fullWidth}
        $isSmall={isSmall}
      >
        <$MinusButtonQuantity
          primary={!isDeleteStep}
          danger={isDeleteStep}
          icon={isDeleteStep ? <Trash /> : <MinusIcon />}
          onClick={handleDecrementQuantity}
          disabled={transitionDisabled || parseInt(value.toString()) === min}
          dataTestId={
            dataTestId ? `${dataTestId}quantityDecrement` : 'quantityDecrement'
          }
          type={BUTTON_TYPES.BUTTON}
          $isSmall={isSmall}
          ariaLabel={messages.minusButton}
        />
        <$QuantityDisplay
          $disabled={transitionDisabled}
          $fullWidth={fullWidth}
          $currentValue={currentQuantity}
          $maxValue={max !== undefined ? customMax : undefined}
          $isFocused={isFocused}
          $status={status}
          onClick={() => {
            if (combinedRef && combinedRef?.current) {
              combinedRef?.current.focus();
              setIsFocused(true);
            }
          }}
          $isSmall={isSmall}
        >
          <Input
            type="text"
            max={max}
            name={name}
            onChange={handleEditQuantity}
            value={currentQuantity}
            isDisabled={transitionDisabled}
            ref={combinedRef}
            dataTestId="quantityInput"
            componentThemeName={SUPPORTED_COMPONENTS.QUANTITY_INPUT}
            selectAllOnFocus={selectAllOnFocus}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            isCentered
            isRounded={false}
            isFocusAndHoverBehavioursHandled={false}
            isSmall={isSmall}
          />
        </$QuantityDisplay>
        <$PlusButtonQuantity
          primary
          icon={<PlusIcon />}
          onClick={handleIncrementQuantity}
          disabled={
            transitionDisabled || parseInt(value.toString()) === customMax
          }
          dataTestId={
            dataTestId ? `${dataTestId}quantityIncrement` : 'quantityIncrement'
          }
          type={BUTTON_TYPES.BUTTON}
          $isSmall={isSmall}
          ariaLabel={messages.plusButton}
        />
      </$QuantityContainer>
      <FieldMessage message={message} status={status} />
    </>
  );
};

export default React.forwardRef<HTMLInputElement, QuantityInputProps>(
  (props, ref) => <QuantityInput forwardedRef={ref} {...props} />,
);
